import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import { getCurrentMonthYear } from '../utils/currentDate';

const PrivacidadePage = () => {
  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Política de Privacidade — Calculadora de Horas</title>
        <meta name="description" content="Política de privacidade da Calculadora de Horas conforme a LGPD. Veja como coletamos, usamos e protegemos seus dados, cookies, Google Analytics e PostHog." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/politica-privacidade" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Política de Privacidade — Calculadora de Horas" />
        <meta property="og:description" content="Política de privacidade da Calculadora de Horas conforme a LGPD. Veja como coletamos, usamos e protegemos seus dados, cookies, Google Analytics e PostHog." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/politica-privacidade" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Política de Privacidade — Calculadora de Horas" />
        <meta name="twitter:description" content="Política de privacidade da Calculadora de Horas conforme a LGPD. Veja como coletamos, usamos e protegemos seus dados, cookies, Google Analytics e PostHog." />

        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/politica-privacidade" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/politica-privacidade" />
      </Helmet>
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Política de Privacidade","item":"https://calculadoradehoras.net.br/politica-privacidade"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4" aria-label="Breadcrumb">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Política de Privacidade</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Política de Privacidade
          </h1>
          <p className="text-body-text" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <article className="prose prose-lg max-w-none bg-white rounded-2xl shadow-lg p-8 space-y-6">
          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">1. Introdução</h2>
            <p className="text-body-text leading-relaxed">
              A sua privacidade é importante para nós. Esta Política de Privacidade explica como a Calculadora de Horas coleta, usa e protege suas informações pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">2. Dados Coletados</h2>
            <p className="text-body-text leading-relaxed mb-3">
              <strong>Dados de Uso:</strong> Coletamos informações sobre como você usa nosso site, incluindo páginas visitadas, tempo de permanência e interações com as calculadoras.
            </p>
            <p className="text-body-text leading-relaxed mb-3">
              <strong>Cookies:</strong> Utilizamos cookies para melhorar sua experiência de navegação e para fins analíticos.
            </p>
            <p className="text-body-text leading-relaxed mb-3">
              <strong>Formulário de contato:</strong> Se você nos escrever pela página de contato, recebemos seu nome, e-mail e mensagem. Esses dados são enviados ao nosso e-mail por meio do serviço FormSubmit (formsubmit.co) e usados apenas para responder ao seu contato.
            </p>
            <p className="text-body-text leading-relaxed">
              <strong>Importante:</strong> Não coletamos ou armazenamos os valores que você insere nas calculadoras. Todos os cálculos são feitos localmente no seu navegador.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">3. Uso de Dados</h2>
            <p className="text-body-text leading-relaxed">
              Os dados coletados são utilizados para:
            </p>
            <ul className="list-disc list-inside space-y-2 text-body-text">
              <li>Melhorar a funcionalidade e usabilidade do site</li>
              <li>Analisar o uso do site através do Google Analytics e do PostHog</li>
              <li>Exibir anúncios relevantes através do Google AdSense</li>
              <li>Responder a solicitações de contato</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">4. Google Analytics, AdSense e PostHog</h2>
            <p className="text-body-text leading-relaxed">
              Utilizamos o Google Analytics para análise de tráfego e o Google AdSense para exibição de anúncios. Esses serviços podem coletar informações anônimas sobre sua visita, incluindo endereço IP, tipo de navegador e páginas visitadas.
            </p>
            <p className="text-body-text leading-relaxed mt-3">
              <strong>PostHog (gravação de sessão):</strong> O site também utiliza o PostHog, ferramenta de análise de produto fornecida pela plataforma em que o site é hospedado. Ela registra como você interage com as páginas — por exemplo, cliques, rolagem e navegação entre páginas — e pode gerar gravações da sessão para entender o uso do site e identificar erros. Os dados são processados em servidores do PostHog localizados nos Estados Unidos. Não usamos essas informações para identificar você pessoalmente.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">5. Cookies</h2>
            <p className="text-body-text leading-relaxed">
              Cookies são pequenos arquivos de texto armazenados no seu navegador. Você pode desativar os cookies nas configurações do seu navegador, mas isso pode afetar a funcionalidade do site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">6. Seus Direitos (LGPD)</h2>
            <p className="text-body-text leading-relaxed">
              De acordo com a LGPD, você tem direito a:
            </p>
            <ul className="list-disc list-inside space-y-2 text-body-text">
              <li>Confirmar a existência de tratamento de dados pessoais</li>
              <li>Acessar seus dados pessoais</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
              <li>Solicitar a eliminação de dados pessoais</li>
              <li>Revogar o consentimento</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">7. Segurança</h2>
            <p className="text-body-text leading-relaxed">
              Implementamos medidas de segurança técnicas e administrativas para proteger seus dados contra acesso não autorizado, alteração, divulgação ou destruição.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">8. Contato</h2>
            <p className="text-body-text leading-relaxed">
              Para exercer seus direitos ou esclarecer dúvidas sobre esta Política de Privacidade, entre em contato conosco através da página de contato.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-3">9. Alterações</h2>
            <p className="text-body-text leading-relaxed">
              Esta Política de Privacidade pode ser atualizada periodicamente. Recomendamos que você revise esta página regularmente para se manter informado sobre como protegemos seus dados.
            </p>
          </section>
        </article>
      </main>

      <Footer />
    </>
  );
};

export default PrivacidadePage;
